/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useMemo } from 'react';
import { Text } from '@/shared/ui';

const COLORS = [
  'var(--seed-base)',
  'var(--accent-terracota)',
  'var(--accent-petroleo)',
  'var(--accent-ambar)',
  'var(--accent-mostarda)',
  'var(--accent-salvia)',
];

const PIECE_COUNT = 28;
const LIFETIME_MS = 1700;

interface Piece {
  id: number;
  left: number;
  delay: number;
  duration: number;
  color: string;
  rotate: number;
  size: number;
}

export interface ConfettiBurstProps {
  /** Fired once the burst has finished, so the caller can unmount it. */
  onDone: () => void;
}

/**
 * A brief, dependency-free confetti burst for a moment worth celebrating —
 * a sale closing, here. Self-clears after {@link LIFETIME_MS} so callers
 * never have to remember to unmount it.
 *
 * @param props.onDone - Called once the animation has run its course.
 * @returns The confetti overlay.
 */
export function ConfettiBurst({ onDone }: ConfettiBurstProps) {
  useEffect(() => {
    const timer = setTimeout(onDone, LIFETIME_MS);
    return () => clearTimeout(timer);
  }, [onDone]);

  const pieces = useMemo<Piece[]>(
    () => Array.from({ length: PIECE_COUNT }, (_, id) => ({
      id,
      left: Math.random() * 100,
      delay: Math.random() * 0.25,
      duration: 1.1 + Math.random() * 0.5,
      color: COLORS[id % COLORS.length],
      rotate: Math.random() * 360,
      size: 6 + Math.random() * 5,
    })),
    [],
  );

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[10050] overflow-hidden"
      aria-hidden="true"
      data-testid="sale-conclude-confetti"
    >
      {pieces.map((p) => (
        <Text
          component="span"
          key={p.id}
          className="absolute top-[-12px] rounded-(--radius-sm)"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 1.6,
            background: p.color,
            animation: `sale-confetti-fall ${p.duration}s ease-in ${p.delay}s forwards`,
            transform: `rotate(${p.rotate}deg)`,
          }}
        />
      ))}
      <style>{`
        @keyframes sale-confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default ConfettiBurst;
